import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SITE_URL } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { ProductCard } from "@/components/product-card";
import { ProductSkeleton, CreatorSkeleton } from "@/components/skeleton-card";
import { ArrowRight, Sparkles } from "lucide-react";
import { fetchCreators, fetchProducts, fetchNewProducts, fetchFeaturedLooks, fetchTrending } from "@/lib/queries";
import { fetchPersonalizedRecommendations } from "@/lib/recommendations.functions";
import { resolveImage } from "@/lib/image-map";
import { Reveal } from "@/components/reveal";
import { useAuth } from "@/hooks/use-auth";
import { useRoles } from "@/hooks/use-roles";
import { DeviceCarousel } from "@/components/device-carousel";
import { FlipWords } from "@/components/flip-words";
import { TextGenerateEffect } from "@/components/text-generate-effect";
import Waves from "@/components/waves-lazy";
import FlowingMenu, { type FlowingMenuItem } from "@/components/FlowingMenu";
import { ScrollMarquee } from "@/components/scroll-marquee";
import LogoLoop from "@/components/LogoLoop";
import { LiveCursors } from "@/components/live-cursors";
import {
  SiShopify,
  SiEtsy,
  SiTarget,
  SiWalmart,
  SiZalando,
  SiStripe,
  SiPaypal,
  SiApplepay,
  SiGooglepay,
  SiKlarna,
  SiAmericanexpress,
  SiVisa,
  SiMastercard,
  SiAfterpay,
} from "react-icons/si";
import { FaAmazon } from "react-icons/fa";
import { supabase } from "@/integrations/supabase/client";
import { lookImageUrl } from "@/lib/storage";
import hero1 from "@/assets/hero-1.jpg";
import sweaterImg from "@/assets/product-sweater.jpg";
import collectionBeauty from "@/assets/collection-beauty.jpg";
import collectionFashion from "@/assets/collection-fashion.jpg";
import collectionHome from "@/assets/collection-home.jpg";
import productNecklace from "@/assets/product-necklace.jpg";
import productPerfume from "@/assets/product-perfume.jpg";
import productCream from "@/assets/product-cream.jpg";
import creator1 from "@/assets/creator-1.jpg";
import creator2 from "@/assets/creator-2.jpg";
import creator3 from "@/assets/creator-3.jpg";



export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Pluggz – Where Creators, Brands & Fans Connect" },
      { name: "description", content: "Pluggz is a social affiliate ecommerce platform where creators build personalized storefronts and shoppers discover their favorite products." },
      { name: "keywords", content: "creator affiliate platform UK, influencer storefront, social commerce, shoppable creator collections, shop influencer curations, curated fashion marketplace, link-in-bio ecommerce, womens fashion influencer, beauty skincare affiliate, interactive social shopping, influencer monetization platform, affiliate network for creators, performance-based influencer marketing, server-to-server tracking, creator economy, high-commission affiliate programs" },
      { property: "og:title", content: "Pluggz – Where Creators, Brands & Fans Connect" },
      { property: "og:description", content: "Pluggz is a social affiliate ecommerce platform where creators build personalized storefronts and shoppers discover their favorite products." },
      { property: "og:url", content: `${SITE_URL}/` },
      { property: "og:image", content: hero1 },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Pluggz – Where Creators, Brands & Fans Connect" },
      { name: "twitter:description", content: "Pluggz is a social affiliate ecommerce platform where creators build personalized storefronts and shoppers discover their favorite products." },
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}/` },
      { rel: "preload", as: "image", href: sweaterImg, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "Pluggz",
              url: `${SITE_URL}/`,
              logo: `${SITE_URL}/favicon.png`,
              description: "A social affiliate ecommerce platform connecting creators, brands and consumers.",
              sameAs: [
                "https://www.tiktok.com/@pluggzofficial",
                "https://www.instagram.com/pluggzofficial",
              ],
            },
            {
              "@type": "WebSite",
              name: "Pluggz",
              url: `${SITE_URL}/`,
              potentialAction: {
                "@type": "SearchAction",
                target: `${SITE_URL}/discover?q={search_term_string}`,
                "query-input": "required name=search_term_string",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  const { user } = useAuth();
  const { isAdmin, isCreator, loading: rolesLoading } = useRoles();
  const isShopper = !!user && !rolesLoading && !isAdmin && !isCreator;

  const shopperProfile = useQuery({
    queryKey: ["shopper-profile", user?.id],
    enabled: isShopper,
    queryFn: async () => {
      const { data } = await supabase
        .from("shopper_profiles")
        .select("preferred_categories, followed_creators")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data as { preferred_categories: string[]; followed_creators: string[] } | null;
    },
  });

  const preferredCats = shopperProfile.data?.preferred_categories ?? [];
  const followedIds = shopperProfile.data?.followed_creators ?? [];

  const forYou = useQuery({
    queryKey: ["for-you-products", preferredCats],
    enabled: isShopper && preferredCats.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("products")
        .select("*")
        .in("category", preferredCats)
        .order("created_at", { ascending: false })
        .limit(6);
      return (data ?? []) as any[];
    },
  });

  const followedProducts = useQuery({
    queryKey: ["followed-creator-products", followedIds],
    enabled: isShopper && followedIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("creator_products")
        .select("created_at, product:products(*), creator:creators!creator_id(handle, name)")
        .in("creator_id", followedIds)
        .order("created_at", { ascending: false })
        .limit(6);
      return (data ?? [])
        .map((row: any) => row.product ? {
          ...row.product,
          creator_handle: row.creator?.handle ?? null,
          creator_name: row.creator?.name ?? null,
        } : null)
        .filter(Boolean);
    },
  });

  const { data: live = [], isLoading: liveLoading } = useQuery({ queryKey: ["creators", "live"], queryFn: () => fetchCreators({ status: ["live"] }) });
  const { data: products = [], isLoading: productsLoading } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });
  const { data: trending = [], isLoading: trendingLoading } = useQuery({
    queryKey: ["trending-products"],
    queryFn: fetchTrending,
    staleTime: 15 * 60_000,
  });
  const showTrendingSkeleton = trendingLoading || productsLoading;
  const { data: newProducts = [], isLoading: newLoading } = useQuery({ queryKey: ["products", "new"], queryFn: fetchNewProducts });
  const { data: featuredLooks = [] } = useQuery({ queryKey: ["featured-looks"], queryFn: fetchFeaturedLooks });
  const { data: recommended } = useQuery({
    queryKey: ["recommended-for-you", user?.id ?? "anon"],
    queryFn: () =>
      fetchPersonalizedRecommendations({ data: { userId: user?.id, limit: 8 } }),
    staleTime: 10 * 60_000,
  });
  const recommendedProducts = recommended?.products ?? [];

  const categoryItems: FlowingMenuItem[] = [
    { link: "/category/womens-fashion", text: "Women's Fashion", image: collectionFashion },
    { link: "/category/beauty-skincare", text: "Beauty & Skincare", image: collectionBeauty },
    { link: "/category/home-decor", text: "Home & Decor", image: collectionHome },
    { link: "/category/shoes-accessories", text: "Shoes & Accessories", image: productNecklace },
    { link: "/category/travel", text: "Travel with me", image: hero1 },
    { link: "/category/fitness-wellness", text: "Fitness & Wellness", image: productCream },
    { link: "/category/mother-baby", text: "Mother & Baby", image: creator2 },
    { link: "/category/cooking-grocery", text: "Cooking & Grocery", image: creator3 },
    { link: "/category/finance-lifestyle", text: "Finance & Lifestyle", image: productPerfume },
    { link: "/category/grwm", text: "GRWM", image: creator1 },
  ];


  return (
    <div>
      <section className="relative overflow-x-hidden">
        <div className="pointer-events-none absolute inset-0 mesh-bg opacity-70" aria-hidden />
        <div className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full bg-accent/20 blur-3xl float-slow" aria-hidden />
        <div className="pointer-events-none absolute right-10 bottom-10 h-80 w-80 rounded-full bg-sand/30 blur-3xl float-slow" style={{ animationDelay: "-3s" }} aria-hidden />
        <div className="pointer-events-none absolute inset-0">
          <LiveCursors roomId="homepage" />
        </div>

        <div className="relative grid min-h-[600px] grid-cols-1 md:min-h-[700px] md:grid-cols-12">
          <div className="relative order-1 flex flex-col justify-between px-4 py-6 sm:px-6 sm:py-12 md:order-1 md:col-span-5 md:px-14 md:py-20">
            <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.35] [mask-image:radial-gradient(ellipse_at_center,black_55%,transparent_85%)]">
              <Waves
                lineColor="rgba(139, 115, 85, 0.55)"
                backgroundColor="transparent"
                waveSpeedX={0}
                waveSpeedY={0.01}
                waveAmpX={40}
                waveAmpY={20}
                friction={0.9}
                tension={0.01}
                maxCursorMove={120}
                xGap={12}
                yGap={36}
              />
            </div>
            <div className="relative flex flex-1 flex-col justify-center">
              <Reveal delay={120}>
                <h1 className="font-serif text-[2rem] leading-[1.05] sm:text-5xl md:text-7xl break-words text-foreground">
                  The things our{" "}
                  <FlipWords
                    words={["favourite people", "creators", "editors", "curators", "insiders"]}
                    className="italic text-accent"
                    duration={2800}
                  />
                  <br />
                  actually use.
                </h1>
              </Reveal>
              <Reveal delay={260}>
                <TextGenerateEffect
                  words="A curated marketplace of creator-led storefronts. Two taps from inspiration to checkout — on the brand's own site."
                  className="mt-5 max-w-md text-sm sm:text-base text-muted-foreground"
                  duration={0.4}
                  staggerDelay={0.04}
                />
              </Reveal>
              <Reveal delay={380}>
                <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
                  <Link to="/discover" className="group inline-flex items-center gap-2 border-b border-foreground pb-1 text-sm tracking-wide">
                    Enter the discovery feed
                    <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                  </Link>
                  <Link to="/apply" className="link-underline text-sm text-muted-foreground hover:text-foreground">Apply as a creator</Link>
                </div>
              </Reveal>
            </div>
            <Reveal delay={520} className="relative hidden text-xs text-muted-foreground md:block">
              <p>50–150 vetted creators · Pure affiliate · Brand checkout</p>
            </Reveal>
          </div>
          <div className="relative order-2 flex flex-col px-4 py-8 min-h-[420px] sm:px-6 sm:py-12 md:order-2 md:col-span-7 md:px-14 md:py-20 md:min-h-0">
            <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.35] [mask-image:radial-gradient(ellipse_at_center,black_55%,transparent_85%)]">
              <Waves
                lineColor="rgba(139, 115, 85, 0.55)"
                backgroundColor="transparent"
                waveSpeedX={0}
                waveSpeedY={0.01}
                waveAmpX={40}
                waveAmpY={20}
                friction={0.9}
                tension={0.01}
                maxCursorMove={120}
                xGap={12}
                yGap={36}
              />
            </div>
            <div className="relative z-10 flex h-full w-full items-center justify-center">
              <DeviceCarousel products={products.length ? products : trending} creator={live[0]} />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-16 sm:pt-24">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="editorial-eyebrow">Live now</p>
              <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Storefronts open today</h2>
            </div>
            <Link to="/creators" className="link-underline text-sm text-muted-foreground hover:text-foreground">All creators →</Link>
          </div>
        </Reveal>
        <div className="mt-8 grid gap-6 sm:gap-8 sm:grid-cols-2 md:grid-cols-3">
          {liveLoading ? (
            <>
              <CreatorSkeleton />
              <CreatorSkeleton />
              <CreatorSkeleton />
            </>
          ) : (
            live.map((c, i) => (
              <Reveal key={c.handle} delay={i * 120}>
                <Link to="/creator/$handle" params={{ handle: c.handle }} className="group card-lift block border border-border bg-card overflow-hidden rounded-sm">
                  <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                    <img src={resolveImage(c.cover_url)} alt={c.name} loading="lazy" width={400} height={500} className="ken-burns h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
                    <div className="absolute left-4 top-4 flex items-center gap-2 bg-background/90 px-2.5 py-1 text-[10px] uppercase tracking-widest backdrop-blur">
                      <span className="h-1.5 w-1.5 rounded-full bg-destructive animate-pulse" /> Live
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-3">
                    <img src={resolveImage(c.avatar_url)} alt={c.name} width={40} height={40} loading="lazy" className="h-10 w-10 rounded-full object-cover ring-1 ring-border" />
                      <div>
                        <p className="font-serif text-lg leading-tight">{c.name}</p>
                        <p className="text-xs text-muted-foreground">@{c.handle} · {c.followers} followers</p>
                      </div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-16 sm:pt-24">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="editorial-eyebrow">Explore by category</p>
              <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Browse the vibe</h2>
            </div>
            <Link to="/discover" className="link-underline text-sm text-muted-foreground hover:text-foreground">All products →</Link>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="mt-8 h-[320px] overflow-hidden rounded-sm border border-border bg-background shadow-[0_30px_80px_-40px_rgba(26,26,26,0.25)] sm:h-[460px] md:h-[600px]" style={{ touchAction: "pan-y" }}>
            <FlowingMenu
              items={categoryItems}
              speed={18}
              bgColor="var(--background)"
              textColor="var(--foreground)"
              marqueeBgColor="var(--foreground)"
              marqueeTextColor="var(--background)"
              borderColor="var(--border)"
            />
          </div>
        </Reveal>
      </section>

      {featuredLooks.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-16 sm:pt-24">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="editorial-eyebrow">Featured stories</p>
                <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Looks worth your time</h2>
              </div>
              <Link to="/discover" className="link-underline text-sm text-muted-foreground hover:text-foreground">Browse all →</Link>
            </div>
          </Reveal>
          <div className="mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 md:grid md:grid-cols-3 md:overflow-visible">
            {featuredLooks.map((look, i) => {
              const publicUrl = lookImageUrl(look.image_path);
              return (
                <Reveal key={look.id} delay={i * 120} className="min-w-[85vw] snap-start md:min-w-0">
                  <Link to="/creator/$handle" params={{ handle: look.creator_handle }} className="group relative block aspect-[4/5] overflow-hidden bg-muted border border-border bg-card rounded-sm">
                    <img
                      src={publicUrl}
                      alt={look.title ?? ""}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="text-[10px] uppercase tracking-widest text-white/80">@{look.creator_handle}</p>
                      <h3 className="mt-2 font-serif text-2xl leading-tight text-white">{look.title}</h3>
                      <span className="mt-4 inline-flex items-center gap-2 border-b border-white/60 pb-1 text-sm text-white transition-colors group-hover:border-white">
                        Shop the look
                        <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {isShopper && (forYou.data?.length ?? 0) > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-16 sm:pt-24">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="editorial-eyebrow">For you</p>
                <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Picked for your taste</h2>
              </div>
              <Link to="/shopper-profile" className="link-underline text-sm text-muted-foreground hover:text-foreground">Edit preferences →</Link>
            </div>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6 sm:gap-y-12 md:grid-cols-3 lg:grid-cols-6">
            {forYou.data!.map((p: any, i: number) => (
              <Reveal key={p.id} delay={i * 60}><ProductCard product={p} /></Reveal>
            ))}
          </div>
        </section>
      )}

      {isShopper && (followedProducts.data?.length ?? 0) > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-16 sm:pt-24">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="editorial-eyebrow">From creators you follow</p>
                <h2 className="mt-2 font-serif text-3xl sm:text-4xl">New from your follows</h2>
              </div>
              <Link to="/shopper-profile" className="link-underline text-sm text-muted-foreground hover:text-foreground">Manage follows →</Link>
            </div>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6 sm:gap-y-12 md:grid-cols-3 lg:grid-cols-6">
            {followedProducts.data!.map((p: any, i: number) => (
              <Reveal key={p.id} delay={i * 60}><ProductCard product={p} /></Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-20 pb-16 sm:pt-32 sm:pb-24">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="editorial-eyebrow">Trending this week</p>
              <h2 className="mt-2 font-serif text-3xl sm:text-4xl">What's getting clicked</h2>
            </div>
            <Link to="/discover" className="link-underline text-sm text-muted-foreground hover:text-foreground">Browse all →</Link>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6 sm:gap-y-12 md:grid-cols-4">
          {showTrendingSkeleton ? (
            <>
              <ProductSkeleton />
              <ProductSkeleton />
              <ProductSkeleton />
              <ProductSkeleton />
            </>
          ) : (
            trending.map((p, i) => (
              <Reveal key={p.id} delay={i * 90}>
                <ProductCard product={p} />
              </Reveal>
            ))
          )}
        </div>
      </section>

      {recommendedProducts.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-16 sm:pb-24">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="editorial-eyebrow">
                  {user ? "Recommended for you" : "You might love"}
                </p>
                <h2 className="mt-2 font-serif text-3xl sm:text-4xl">
                  {user ? "Picked from what you browse" : "Editor-picked starters"}
                </h2>
              </div>
              <Link to="/discover" className="link-underline text-sm text-muted-foreground hover:text-foreground">Browse all →</Link>
            </div>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6 sm:gap-y-12 md:grid-cols-4">
            {recommendedProducts.slice(0, 8).map((p, i) => (
              <Reveal key={p.id} delay={i * 60}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="w-full pt-16 sm:pt-24 pb-16 sm:pb-20">
        <Reveal>
          <div className="border-y border-foreground/10 bg-background py-10 sm:py-16 px-4 sm:px-6">
            <p className="editorial-eyebrow text-center">Shop direct · pay your way</p>
            <h3 className="mt-2 text-center font-serif text-2xl sm:text-3xl">Brands & checkouts<br />we plug into</h3>
            <p className="mx-auto mt-3 max-w-xl text-center text-sm text-muted-foreground">
              Pluggz sends you straight to the retailer. Check out with the wallet you already trust.
            </p>

            <div className="mt-12 space-y-12 text-foreground">
              <div>
                <p className="mb-4 text-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Retail partners</p>
                <LogoLoop
                  ariaLabel="Retail partners"
                  speed={22}
                  logoHeight={34}
                  gap={72}
                  logos={[
                    { node: <span style={{ color: "#FF9900" }}><FaAmazon /></span>, title: "Amazon", href: "https://www.amazon.com" },
                    { node: <span style={{ color: "#96BF48" }}><SiShopify /></span>, title: "Shopify", href: "https://www.shopify.com" },
                    { node: <span style={{ color: "#F56400" }}><SiEtsy /></span>, title: "Etsy", href: "https://www.etsy.com" },
                    { node: <span style={{ color: "#CC0000" }}><SiTarget /></span>, title: "Target", href: "https://www.target.com" },
                    { node: <span style={{ color: "#0071CE" }}><SiWalmart /></span>, title: "Walmart", href: "https://www.walmart.com" },
                    { node: <span style={{ color: "#FF6900" }}><SiZalando /></span>, title: "Zalando", href: "https://www.zalando.com" },
                  ]}
                />
              </div>
              <div className="relative pt-10">
                <div className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-foreground/20 to-transparent" aria-hidden />
                <p className="mb-4 text-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Payment methods</p>
                <LogoLoop
                  ariaLabel="Payment partners"
                  speed={18}
                  direction="right"
                  logoHeight={30}
                  gap={64}
                  logos={[
                    { node: <span style={{ color: "#635BFF" }}><SiStripe /></span>, title: "Stripe", href: "https://stripe.com" },
                    { node: <span style={{ color: "#003087" }}><SiPaypal /></span>, title: "PayPal", href: "https://www.paypal.com" },
                    { node: <span style={{ color: "#000000" }}><SiApplepay /></span>, title: "Apple Pay", href: "https://www.apple.com/apple-pay/" },
                    { node: <span style={{ color: "#5F6368" }}><SiGooglepay /></span>, title: "Google Pay", href: "https://pay.google.com" },
                    { node: <span style={{ color: "#FFB3C7" }}><SiKlarna /></span>, title: "Klarna", href: "https://www.klarna.com" },
                    { node: <span style={{ color: "#B2F199" }}><SiAfterpay /></span>, title: "Afterpay", href: "https://www.afterpay.com" },
                    { node: <span style={{ color: "#1A1F71" }}><SiVisa /></span>, title: "Visa" },
                    { node: <span style={{ color: "#EB001B" }}><SiMastercard /></span>, title: "Mastercard" },
                    { node: <span style={{ color: "#006FCF" }}><SiAmericanexpress /></span>, title: "American Express" },
                  ]}
                />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <HowItWorks />

      <section className="mx-auto max-w-4xl px-4 sm:px-6 pb-20 sm:pb-32">
        <Reveal>
          <p className="editorial-eyebrow text-center">Product tour</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-center">See Pluggz in action</h2>
          <p className="mt-4 text-center text-muted-foreground text-sm">A 3-minute walkthrough of the full platform</p>
        </Reveal>

        <Reveal delay={100}>
          <Link
            to="/demo"
            className="mt-10 group flex w-full items-center justify-center gap-3 rounded-xl border border-border bg-foreground px-6 py-5 text-background hover:bg-foreground/90 transition"
          >
            <span className="text-sm sm:text-base font-medium tracking-wide">Take the interactive tour</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-16 sm:pb-24">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="editorial-eyebrow">Just landed</p>
              <h2 className="mt-2 font-serif text-3xl sm:text-4xl"><span className="italic shimmer">New in</span></h2>
            </div>
            <Link to="/discover" className="link-underline text-sm text-muted-foreground hover:text-foreground">See all →</Link>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6 sm:gap-y-12 md:grid-cols-4">
          {newLoading ? (
            <>
              <ProductSkeleton />
              <ProductSkeleton />
              <ProductSkeleton />
              <ProductSkeleton />
            </>
          ) : (
            newProducts.map((p, i) => (
              <Reveal key={p.id} delay={i * 90}>
                <ProductCard product={p} />
              </Reveal>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

type Tab = "creators" | "shoppers" | "brands";

const TAB_FEATURES: Record<Tab, string[]> = {
  creators: [
    "Your own shoppable storefront",
    "Automatic affiliate link generation",
    "Real-time earnings dashboard",
    "Link in bio page for Instagram and TikTok",
    "Verified creator badge",
  ],
  shoppers: [
    "Discover products curated by creators you trust",
    "Save favourites to your wishlist",
    "Share your wishlist with friends",
    "Shop directly from creator storefronts",
    "New products added daily",
  ],
  brands: [
    "Performance-based commissions only",
    "Products sync automatically from your affiliate network",
    "Featured homepage placement available",
    "Real-time click and conversion tracking",
    "Access to verified UK creators",
  ],
};

const TAB_CTA: Record<Tab, { to: string; label: string }> = {
  creators: { to: "/apply", label: "Apply to become a creator →" },
  shoppers: { to: "/discover", label: "Start discovering →" },
  brands: { to: "/advertise", label: "Explore brand options →" },
};

function HowItWorks() {
  const [tab, setTab] = useState<Tab>("creators");
  const tabs: { id: Tab; label: string }[] = [
    { id: "creators", label: "For Creators" },
    { id: "shoppers", label: "For Shoppers" },
    { id: "brands", label: "For Brands" },
  ];

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-16 sm:pt-24">
      <Reveal>
        <div className="text-center">
          <p className="editorial-eyebrow">The platform</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Everything creators and brands need</h2>
        </div>
      </Reveal>

      <div className="mt-8 flex flex-wrap justify-center gap-1 sm:gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            aria-pressed={tab === t.id}
            className={`border-b-2 px-3 py-2.5 text-xs sm:text-sm sm:px-4 sm:py-3 tracking-wide transition-colors ${
              tab === t.id
                ? "border-foreground text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div key={tab} className="mt-10 grid grid-cols-1 gap-8 sm:gap-12 md:grid-cols-2 md:items-center animate-fade-in transition-opacity duration-300">
        <div>
          <ul className="space-y-5">
            {TAB_FEATURES[tab].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span aria-hidden className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span className="font-serif text-lg leading-snug">{item}</span>
              </li>
            ))}
          </ul>
          <Link to={TAB_CTA[tab].to} className="link-underline mt-8 inline-block text-sm">
            {TAB_CTA[tab].label}
          </Link>
        </div>

        <div className="relative">
          {tab === "creators" && (
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "£840", label: "Avg monthly earnings" },
                { value: "12 mins", label: "To launch your storefront" },
                { value: "1-click", label: "Affiliate link generation" },
                { value: "Real-time", label: "Click & conversion tracking" },
              ].map((k) => (
                <div key={k.label} className="rounded border border-border p-4">
                  <p className="font-serif text-2xl">{k.value}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{k.label}</p>
                </div>
              ))}
            </div>
          )}
          {tab === "shoppers" && (
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              <ProductSkeleton />
              <ProductSkeleton />
              <ProductSkeleton />
              <ProductSkeleton />
            </div>
          )}
          {tab === "brands" && (
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "£12.4k", label: "Platform revenue" },
                { value: "48", label: "Active creators" },
                { value: "2.3k", label: "Monthly clicks" },
                { value: "4.8%", label: "Conversion rate" },
              ].map((k) => (
                <div key={k.label} className="rounded border border-border p-4">
                  <p className="font-serif text-2xl">{k.value}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{k.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
